import React from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';

export interface ToolbarButtonProps {
  icon?: React.ReactNode;
  /** The label. Leave out for an icon-only button — then give it an `aria-label`. */
  children?: React.ReactNode;
  onClick?: () => void;
  /** Explains what the button does, on hover. An icon-only button shows its name if this is left out. */
  tooltip?: string;
  /** Shows a spinner in place of the icon and blocks clicks. */
  loading?: boolean;
  /** The label while loading, e.g. "Preparing". Defaults to the normal label. */
  loadingLabel?: string;
  /** For a button that toggles something open, like a side panel. */
  selected?: boolean;
  /** A small count after the label, e.g. how many filters are applied. Hidden at 0. */
  count?: number;
  /**
   * `outlined` — a bordered button, for toolbars.
   * `ghost` — no border or fill until hovered, for secondary actions inside
   * a header, like a panel's Expand / Collapse.
   * `brand` — filled in the brand's tint, for one offered next step on a
   * screen, e.g. "Save as Agent" above the composer. Use it once at a time.
   * `solid` — filled in the brand colour, for the one action a small form
   * exists for, beside its input: "Send invite". Nothing quieter reads as
   * "press me next" to someone who has just typed into the box.
   */
  variant?: 'outlined' | 'ghost' | 'brand' | 'solid';
  /** `danger` for an action that destroys something: the same red as a danger menu item. */
  tone?: 'default' | 'danger';
  /**
   * `medium` matches a medium input (`NumberField size="medium"`) so a button
   * that acts on a row of inputs — Calculate, Simulate — is the same height as
   * they are and the row reads as one control. Toolbars stay `small`.
   */
  size?: 'small' | 'medium';
  disabled?: boolean;
  'aria-label'?: string;
  /** For disclosure buttons: whether what they control is open. */
  'aria-expanded'?: boolean;
  /** Id of the element the button opens or closes. */
  'aria-controls'?: string;
}

/**
 * The standard small action button for toolbars: Export, Share, Chat.
 *
 * Neutral at rest, so it never outshouts the page. `selected` gives it the
 * brand tint for buttons that open something and stay "on" while it is open;
 * `variant="brand"` wears that tint permanently, for a step being offered
 * rather than a toggle — it carries no pressed state to announce.
 * Without a label it is a square icon button with the same look, for tight
 * spaces; its name shows on hover.
 *
 * Not for the page's single main action — use a contained Button for that.
 */
export function ToolbarButton({
  icon,
  children,
  onClick,
  tooltip,
  loading = false,
  loadingLabel,
  selected,
  count,
  variant = 'outlined',
  tone = 'default',
  size = 'small',
  disabled,
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
}: ToolbarButtonProps) {
  const ghost = variant === 'ghost';
  const brand = variant === 'brand';
  const solid = variant === 'solid';
  const danger = tone === 'danger';
  const iconOnly = children === undefined || children === null;

  const button = iconOnly ? (
    <IconButton
      size="small"
      onClick={onClick}
      disabled={disabled || loading}
      aria-pressed={selected}
      aria-label={ariaLabel ?? tooltip}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      sx={(t) => ({
        width: t.ds.sizes.control.sm,
        height: t.ds.sizes.control.sm,
        flexShrink: 0,
        borderRadius: `${t.ds.radius.sm}px`,
        border: ghost ? 0 : `1px solid ${t.tokens.border.default}`,
        bgcolor: ghost ? 'transparent' : t.tokens.surface.raised,
        color: selected ? t.tokens.text.accent : t.tokens.text.secondary,
        '&:hover': ghost
          ? { bgcolor: t.tokens.brand.primarySofter, color: t.tokens.text.primary }
          : { borderColor: t.tokens.border.strong, bgcolor: t.tokens.surface.raised, color: t.tokens.text.primary },
        ...(brand && {
          border: `1px solid ${t.tokens.brand.primary}`,
          bgcolor: t.tokens.brand.primarySoft,
          color: t.tokens.text.accent,
          '&:hover': { borderColor: t.tokens.brand.primary, bgcolor: t.tokens.brand.primarySoft },
        }),
        ...(danger && {
          color: t.tokens.semantic.danger,
          // Red at rest, not only on hover, so it reads as a danger action
          // before the pointer reaches it. A ghost button has no border.
          borderColor: t.tokens.semantic.danger,
          '&:hover': { bgcolor: t.tokens.semantic.dangerSoft, color: t.tokens.semantic.danger,
            borderColor: t.tokens.semantic.danger },
        }),
      })}
    >
      {loading ? <CircularProgress size={14} color="inherit" /> : icon}
    </IconButton>
  ) : (
    <Button
      variant={ghost ? 'text' : 'outlined'}
      color="inherit"
      size="small"
      onClick={onClick}
      disabled={disabled || loading}
      aria-pressed={selected}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : icon}
      sx={(t) => ({
        minHeight: size === 'medium' ? t.ds.sizes.control.md : t.ds.sizes.control.sm,
        ...(size === 'medium' && { px: 1.75 }),
        flexShrink: 0,
        whiteSpace: 'nowrap',
        color: ghost ? t.tokens.text.secondary : t.tokens.text.primary,
        bgcolor: ghost ? 'transparent' : t.tokens.surface.raised,
        borderColor: t.tokens.border.default,
        '& .MuiButton-startIcon': { color: t.tokens.text.secondary },
        '&:hover': ghost
          ? { bgcolor: t.tokens.brand.primarySofter, color: t.tokens.text.primary }
          : { borderColor: t.tokens.border.strong, bgcolor: t.tokens.surface.raised },
        ...(brand && {
          color: t.tokens.text.accent,
          bgcolor: t.tokens.brand.primarySoft,
          borderColor: t.tokens.brand.primary,
          '& .MuiButton-startIcon': { color: 'inherit' },
          '&:hover': { borderColor: t.tokens.brand.primary, bgcolor: t.tokens.brand.primarySoft },
        }),
        ...(solid && {
          color: t.tokens.brand.onPrimary,
          bgcolor: t.tokens.brand.primary,
          borderColor: t.tokens.brand.primary,
          '& .MuiButton-startIcon': { color: 'inherit' },
          '&:hover': { borderColor: t.tokens.brand.primaryHover, bgcolor: t.tokens.brand.primaryHover },
          // Plainly not ready yet, rather than a faded version of ready.
          '&.Mui-disabled': { color: t.tokens.text.disabled, bgcolor: t.tokens.surface.sunken,
            borderColor: t.tokens.border.subtle },
        }),
        ...(selected && {
          color: t.tokens.text.accent,
          bgcolor: t.tokens.brand.primarySoft,
          borderColor: t.tokens.brand.primary,
          '& .MuiButton-startIcon': { color: 'inherit' },
          '&:hover': { borderColor: t.tokens.brand.primary, bgcolor: t.tokens.brand.primarySoft },
        }),
        ...(danger && {
          color: t.tokens.semantic.danger,
          borderColor: t.tokens.semantic.danger,
          '& .MuiButton-startIcon': { color: 'inherit' },
          '&:hover': { bgcolor: t.tokens.semantic.dangerSoft, borderColor: t.tokens.semantic.danger },
        }),
      })}
    >
      {loading && loadingLabel ? loadingLabel : children}
      {!!count && count > 0 && (
        <Box
          component="span"
          aria-label={`${count} applied`}
          sx={(t) => ({
            ...t.typography.caption,
            ml: 0.75,
            px: 0.75,
            minWidth: 20,
            textAlign: 'center',
            borderRadius: `${t.ds.radius.full}px`,
            bgcolor: t.tokens.brand.primary,
            color: t.tokens.brand.onPrimary,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          })}
        >
          {count}
        </Box>
      )}
    </Button>
  );

  const hint = tooltip ?? (iconOnly ? ariaLabel : undefined);
  if (!hint) return button;
  // The span keeps the tooltip working while the button is disabled.
  return (
    <Tooltip title={hint}>
      <span style={{ display: 'inline-flex' }}>{button}</span>
    </Tooltip>
  );
}
